import api from "../api";
import { validatePhoneNumber } from "../../utils/validators";

export interface ECourse {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail?: string;
  category?: string;
  level?: "Beginner" | "Intermediate" | "Advanced";
  language?: string;
  duration?: {
    estimatedHours: number;
    estimatedMinutes: number;
  };
  pricing?: {
    type: "free" | "paid";
    amount?: number;
    currency?: string;
  };
  registration?: {
    type: "self" | "manual" | "invite-only";
    requiresApproval?: boolean;
    maxStudents?: number;
    enrollmentDeadline?: Date;
  };
  settings?: {
    allowDiscussions?: boolean;
    allowDownloads?: boolean;
    trackProgress?: boolean;
    generateCertificate?: boolean;
  };
  tags?: string[];
  visibility?: "public" | "private" | "branch-only";
  status?:
    | "draft"
    | "pending_approval"
    | "approved"
    | "published"
    | "archived"
    | "suspended"
    | "rejected";
  instructor?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  branch?: string;
  stats?: {
    totalStudents: number;
    completionRate: number;
    averageRating: number;
    totalReviews: number;
  };
  rating?: {
    average: number;
    count: number;
  };
  modules?: {
    _id: string;
    title: string;
    description?: string;
    order: number;
    estimatedDuration: number;
    status: "draft" | "published" | "archived" | "deleted";
    contents?: {
      _id: string;
      title: string;
      description?: string;
      type: string;
      order: number;
      estimatedDuration: number;
      status: string;
    }[];
    settings?: {
      allowSkip?: boolean;
      requireCompletion?: boolean;
      attempts?: number;
    };
    createdAt: Date;
  }[];
  chain?: {
    chainId?: string;
    sequenceNumber?: number;
    isChainFinal?: boolean;
    nextCourseId?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ECourseFormData {
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail?: string;
  category?: string;
  level?: "Beginner" | "Intermediate" | "Advanced";
  language?: string;
  duration?: {
    estimatedHours: number;
    estimatedMinutes: number;
  };
  pricing?: {
    type: "free" | "paid";
    amount?: number;
    currency?: string;
  };
  registration?: {
    type: "self" | "manual" | "invite-only";
    requiresApproval?: boolean;
    maxStudents?: number;
    enrollmentDeadline?: Date;
  };
  settings?: {
    allowDiscussions?: boolean;
    allowDownloads?: boolean;
    trackProgress?: boolean;
    generateCertificate?: boolean;
  };
  tags?: string[];
  visibility?: "public" | "private" | "branch-only";
  chain?: {
    chainId?: string;
    sequenceNumber?: number;
    isChainFinal?: boolean;
    nextCourseId?: string;
  };
}

export interface ECourseResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

class ECourseService {
  private baseUrl = "/elearning/courses";

  // Get teacher's courses
  async getTeacherCourses(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ECourseResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await api.get<ECourseResponse>(
      `${this.baseUrl}?${queryParams.toString()}`
    );

    return response as unknown as ECourseResponse;
  }

  // Create a new course
  async createCourse(courseData: ECourseFormData): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(this.baseUrl, courseData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response as unknown as ECourseResponse;
  }

  // Get course details
  async getCourse(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `${this.baseUrl}/${courseId}`
    );

    return response as unknown as ECourseResponse;
  }

  // Update course
  async updateCourse(
    courseId: string,
    updates: Partial<ECourseFormData>
  ): Promise<ECourseResponse> {
    const response = await api.put<ECourseResponse>(
      `${this.baseUrl}/${courseId}`,
      updates,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as ECourseResponse;
  }

  async uploadThumbnail(
    courseId: string,
    file: File
  ): Promise<ECourseResponse> {
    const formData = new FormData();
    formData.append("thumbnail", file);

    const response = await api.post<ECourseResponse>(
      `${this.baseUrl}/${courseId}/thumbnail`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response as unknown as ECourseResponse;
  }

  // Delete course
  async deleteCourse(courseId: string): Promise<ECourseResponse> {
    const response = await api.delete<ECourseResponse>(
      `${this.baseUrl}/${courseId}`
    );

    return response as unknown as ECourseResponse;
  }

  // Publish course
  async publishCourse(courseId: string): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `${this.baseUrl}/${courseId}/publish`
    );

    return response as unknown as ECourseResponse;
  }

  // Unpublish course
  async unpublishCourse(courseId: string): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `${this.baseUrl}/${courseId}/unpublish`
    );

    return response as unknown as ECourseResponse;
  }

  // Get public courses for enrollment
  async getPublicCourses(params?: {
    page?: number;
    limit?: number;
    category?: string;
    level?: string;
    search?: string;
  }): Promise<ECourseResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await api.get<ECourseResponse>(
      `/elearning/public-courses?${queryParams.toString()}`
    );

    return response as unknown as ECourseResponse;
  }

  // Get course content
  async getCourseContent(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `${this.baseUrl}/${courseId}`
    );

    return response as unknown as ECourseResponse;
  }

  // Create course content
  async createCourseContent(
    courseId: string,
    contentData: any
  ): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `${this.baseUrl}/${courseId}/content`,
      contentData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as ECourseResponse;
  }

  // Student methods
  async getMyEnrolledCourses(params?: {
    page?: number;
    limit?: number;
  }): Promise<ECourseResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await api.get<ECourseResponse>(
      `/elearning/my-courses?${queryParams.toString()}`
    );

    return response as unknown as ECourseResponse;
  }

  async enrollInCourse(
    courseId: string,
    phoneNumber?: string
  ): Promise<ECourseResponse> {
    // Validate phone number if provided
    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      throw new Error(
        "Invalid phone number format. Please use Kenyan phone number format (e.g., 0712345678 or +254712345678)"
      );
    }

    const response = await api.post<ECourseResponse>(
      `/elearning/courses/${courseId}/enroll`,
      phoneNumber ? { phoneNumber } : {}
    );

    return response as unknown as ECourseResponse;
  }

  async requestEnrollment(courseId: string): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `/elearning/courses/${courseId}/request-enrollment`
    );

    return response as unknown as ECourseResponse;
  }

  // Get course by ID (detailed view)
  async getCourseById(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/courses/${courseId}`
    );

    return response as unknown as ECourseResponse;
  }

  // Get public course details for enrollment (no enrollment required)
  async getPublicCourseDetails(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/public-courses/${courseId}`
    );

    return response as unknown as ECourseResponse;
  }

  // Get course enrollment status for current user
  async getCourseEnrollmentStatus(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/courses/${courseId}/enrollment-status`
    );

    return response as unknown as ECourseResponse;
  }

  // Update content progress for a student
  async updateContentProgress(
    courseId: string,
    moduleId: string,
    contentId: string,
    progressData: {
      status?: "not_started" | "in_progress" | "completed";
      progress?: number;
      timeSpent?: number;
      videoProgress?: {
        duration?: number;
        watchedDuration?: number;
        watchPercentage?: number;
      };
    }
  ): Promise<ECourseResponse> {
    const response = await api.put<ECourseResponse>(
      `/elearning/courses/${courseId}/modules/${moduleId}/content/${contentId}/progress`,
      progressData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as ECourseResponse;
  }

  // Get course progress for current student
  async getCourseProgress(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/courses/${courseId}/progress`
    );

    return response as unknown as ECourseResponse;
  }

  // Get assignments for enrolled courses
  async getAssignments(): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(`/elearning/assignments`);

    return response as unknown as ECourseResponse;
  }

  // Get course ratings
  async getCourseRatings(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/courses/${courseId}/ratings`
    );

    return response as unknown as ECourseResponse;
  }

  // Get course rating summary
  async getCourseRatingSummary(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/courses/${courseId}/ratings/summary`
    );

    return response as unknown as ECourseResponse;
  }

  // Certificate methods
  async getStudentCertificates(): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(`/elearning/certificates`);

    return response as unknown as ECourseResponse;
  }

  async generateCertificate(courseId: string): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `/elearning/certificates/generate`,
      { courseId }
    );

    return response as unknown as ECourseResponse;
  }

  async downloadCertificate(certificateId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/certificates/${certificateId}/download`
    );

    return response as unknown as ECourseResponse;
  }

  async verifyCertificate(verificationCode: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `/elearning/certificates/verify/${verificationCode}`
    );

    return response as unknown as ECourseResponse;
  }

  // Admin methods
  async getAllCourses(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ECourseResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await api.get<ECourseResponse>(
      `/elearning/admin/courses?${queryParams.toString()}`
    );

    return response as unknown as ECourseResponse;
  }

  async getPendingApprovalCourses(params?: {
    page?: number;
    limit?: number;
  }): Promise<ECourseResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await api.get<ECourseResponse>(
      `/elearning/admin/courses/pending-approval?${queryParams.toString()}`
    );

    return response as unknown as ECourseResponse;
  }

  async approveCourse(
    courseId: string,
    notes?: string
  ): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `/elearning/admin/courses/${courseId}/approve`,
      { notes }
    );

    return response as unknown as ECourseResponse;
  }

  async reassignCourse(
    courseId: string,
    newInstructorId: string
  ): Promise<ECourseResponse> {
    const response = await api.put<ECourseResponse>(
      `${this.baseUrl}/${courseId}/reassign`,
      { newInstructorId }
    );

    return response as unknown as ECourseResponse;
  }

  async rejectCourse(
    courseId: string,
    notes?: string
  ): Promise<ECourseResponse> {
    const response = await api.post<ECourseResponse>(
      `/elearning/admin/courses/${courseId}/reject`,
      { notes }
    );

    return response as unknown as ECourseResponse;
  }

  async getCourseApprovalHistory(courseId: string): Promise<ECourseResponse> {
    const response = await api.get<ECourseResponse>(
      `${this.baseUrl}/courses/${courseId}/approval-history`
    );

    return response as unknown as ECourseResponse;
  }

  async getCoursesAnalytics(
    page = 1,
    limit = 10
  ): Promise<{
    success: boolean;
    data: any[];
    summary: {
      totalRevenue: number;
      totalEnrollments: number;
      averageCompletionRate: number;
      totalCourses: number;
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await api.get<{
      success: boolean;
      data: any[];
      summary: any;
      pagination: any;
    }>(`/elearning/analytics/courses?${params.toString()}`);

    // The axios interceptor already extracts response.data
    // So response is { success: true, data: [...], summary: {...}, pagination: {...} }
    return {
      success: response.success,
      data: response.data || [],
      summary: response.summary || {
        totalRevenue: 0,
        totalEnrollments: 0,
        averageCompletionRate: 0,
        totalCourses: 0,
      },
      pagination: {
        currentPage: response.pagination?.currentPage || 1,
        totalPages: response.pagination?.totalPages || 1,
        totalItems: response.pagination?.totalItems || 0,
        hasNext: response.pagination?.hasNext || false,
        hasPrev: response.pagination?.hasPrev || false,
      },
    };
  }
}

export const eCourseService = new ECourseService();
export default eCourseService;
